/**
 * The one-letter flags a row wears, and the key that spells them out.
 *
 * One table, read by the rows and by the key alike -- for the reason timeline/legend.ts derives
 * its swatches from `theme` rather than restating them. A key typed a second time goes
 * stale the first time a mark moves, and it goes stale silently: it still looks like a key.
 */

import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Badge as MantineBadge, List, type BadgeProps, type BadgeVariant } from "@mantine/core";
import { Hint } from "./Hint";

interface BadgeSpec {
  /** What the row shows. One character, except the percentage the tracker fills in. */
  mark: string;
  /** The hue. Most are inert -- the letter carries it; two are where the colour is it.
   *  `variant` is spelled into the type because Mantine keeps it off `BadgeProps`: it is
   *  the polymorphic wrapper that accepts it, not the props interface. */
  props: BadgeProps & { variant: BadgeVariant };
  /** Catalogue key for the long form: the row's `title`, and the key's own text. A key and
   *  not the copy, because a module-level constant would freeze the language at import. */
  hintKey: string;
}

/**
 * State on a recessed rectangle rather than in colour.
 *
 * Badges sit in a row, and a row of coloured words reads as noise however carefully each hue
 * was chosen -- the letter carries the meaning. So most flags share this one fill, and the two
 * where the colour *is* the meaning say so below.
 */
const SUNKEN: BadgeSpec["props"] = {
  variant: "filled",
  color: "sunken",
  // The fill sets the ink around it, so the letter has to say the body colour back.
  c: "var(--mantine-color-text)",
};

export const BADGES = {
  track: { mark: "J", props: SUNKEN, hintKey: "badges.track" },
  live: { mark: "L", props: { variant: "filled", color: "danger" }, hintKey: "badges.live" },
  heard: { mark: "H", props: SUNKEN, hintKey: "badges.heard" },
  partial: { mark: "%", props: SUNKEN, hintKey: "badges.partial" },
  auto: { mark: "A", props: SUNKEN, hintKey: "badges.auto" },
  unlinked: { mark: "U", props: SUNKEN, hintKey: "badges.unlinked" },
  starred: { mark: "★", props: { variant: "filled", color: "star" }, hintKey: "badges.starred" },
  expired: { mark: "E", props: { ...SUNKEN, opacity: 0.8 }, hintKey: "badges.expired" },
} satisfies Record<string, BadgeSpec>;

export type BadgeKey = keyof typeof BADGES;

/**
 * One flag on a row.
 *
 * `mark` and `hint` override the table where the row has a number to say: the tracker's badge
 * is the percentage itself, and its long form carries the same figure.
 */
export function Badge({
  of,
  mark,
  hint,
  style,
}: {
  of: BadgeKey;
  mark?: string;
  hint?: string;
  /** The legend's mark column, and nothing else -- see `BadgeLegend`. */
  style?: CSSProperties;
}) {
  const { t } = useTranslation();
  const spec = BADGES[of];
  return (
    // The theme's `Badge` defaults are a rounded, sentence-case pill, so a flag has to say
    // `radius` and `tt` back for itself.
    <MantineBadge
      size="sm"
      radius="sm"
      tt="uppercase"
      lts="0.06em"
      title={hint ?? t(spec.hintKey)}
      {...spec.props}
      style={style}
    >
      {mark ?? spec.mark}
    </MantineBadge>
  );
}

/**
 * The key for a list's flags, behind the same "i" the timeline's colour key hangs off.
 *
 * Every line is a real `Badge` rather than a picture of one, so a mark here cannot drift from
 * the rows it describes two inches below. Each list passes the flags it can actually show:
 * naming one that never appears is worse than saying nothing, because it implies the reader
 * has missed a mark somewhere.
 */
export function BadgeLegend({ of }: { of: readonly BadgeKey[] }) {
  const { t } = useTranslation();
  return (
    <Hint aria-label={t("badges.legendLabel")}>
      <List
        listStyleType="none"
        fz={11}
        c="dimmed"
        style={{ display: "flex", flexDirection: "column", gap: 7 }}
      >
        {of.map((key) => (
          // The row is stated on `itemLabel`, not on `itemWrapper` -- do not move it back.
          // A `List.Item` is three elements: the `li`, an `itemWrapper` div that is a
          // shrink-to-fit inline-flex column, and one `itemLabel` span holding *both*
          // children. So the wrapper has a single child and cannot space the mark from its
          // meaning, and it resets `white-space` to normal on the way past; the label is the
          // only node that is actually the parent of the two things being laid out.
          <List.Item
            key={key}
            styles={{
              itemLabel: { display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" },
            }}
          >
            {/* One column for the marks, so the meanings line up: they are a single
                character each except the tracker's, which is a percentage. */}
            <Badge of={key} style={{ flex: "none", minWidth: 26, textAlign: "center" }} />
            {t(BADGES[key].hintKey)}
          </List.Item>
        ))}
      </List>
    </Hint>
  );
}
